import {
  closeCurrentDialog,
  openConfirmDialog,
  openDeleteDialog,
  openDialog,
} from 'platform/components';

// eslint-disable-next-line no-restricted-imports -- [T20-75547]
import {createSearchParams, useNavigate} from 'react-router-dom';

import {isNil} from 'ramda';
import {isFalse, isNotNil, isTrue} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  useArchiveVehicleMutation,
  useGetBusinessCaseQuery,
  useGetVehicleV2Query,
  useUnarchiveVehicleMutation,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useCancelVehicleReservationMutation, useGetSaleVehicleQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {businessCaseRoutes, interestRoutes, testIds, workshopRoutes} from '@dms/routes';
import {
  VehicleReservationWizard,
  handleApiError,
  queryParams,
  useIsLicenceEnabled,
  usePermissions,
  useSaleVehicleActions,
  EntityHeaderAction,
} from '@dms/shared';

import {buildArray, composePath} from 'shared/web';

import {VehicleBuyForm} from '../(sections)/VehicleDetails/components/VehicleBuyForm';
import {useCreationBranchConfirmation} from './useCreationBranchConfirmation';
import {useGetSecondaryVehicleHeaderActions} from './useGetSecondaryVehicleHeaderActions';

// eslint-disable-next-line complexity -- Pre-existing violation
export const useGetVehicleHeaderActions = (vehicleId: string) => {
  const navigate = useNavigate();
  const secondaryActions = useGetSecondaryVehicleHeaderActions(vehicleId);
  const {isSaleActionEnabled} = useSaleVehicleActions(vehicleId);

  const [cancelSaleVehicleReservation] = useCancelVehicleReservationMutation();
  const [unarchiveVehicle, {isLoading: isLoadingArchiveVehicle}] = useUnarchiveVehicleMutation();
  const [archiveVehicle, {isLoading: isLoadingUnarchiveVehicle}] = useArchiveVehicleMutation();

  const {data: saleVehicle} = useGetSaleVehicleQuery({vehicleId});
  const confirmCreationBranch = useCreationBranchConfirmation({
    vehicleBranchId: saleVehicle?.branchId,
  });
  const {data: vehicle} = useGetVehicleV2Query({vehicleId});

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: vehicleId,
  });

  const {data: businessCaseParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.businessCase,
      recordId: saleVehicle?.saleBusinessCaseId ?? '',
    },
    {skip: isNil(saleVehicle?.saleBusinessCaseId)}
  );

  const {data: businessCase} = useGetBusinessCaseQuery(
    {businessCaseId: saleVehicle?.saleBusinessCaseId ?? ''},
    {skip: isNil(saleVehicle?.saleBusinessCaseId)}
  );

  const [
    canReadPurchaseVehicle,
    canReserve,
    canReadActiveReservation,
    canReadReservationHistory,
    canCancelReservation,
    canUpdateReservation,
    hasCreateBusinessCasePurchasePermission,
    hasCreateBusinessCaseSellingPermission,
    hasAddVehicleToBuyPermission,
    canReadSellVehicle,
    hasAddVehicleToSellPermission,
    canUpdateVehicle,
    canReadBusinessCase,
    canCreateServiceCase,
    canCreateInterest,
  ] = usePermissions({
    permissionKeys: [
      'viewBusinessCasePurchase',
      'vehicleReserve',
      'vehicleReadActiveReservation',
      'vehicleReadReservationHistory',
      'vehicleCancelReservation',
      'vehicleUpdateReservation',
      'createBusinessCasePurchase',
      'createBusinessCaseSelling',
      'addVehicleToBuy',
      'viewBusinessCaseSelling',
      'addVehicleToSell',
      'updateVehicle',
      'businessCaseRead',
      'serviceCaseCreate',
      'interestCreate',
    ],
    scopes: {
      vehicleCancelReservation: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      vehicleUpdateReservation: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      vehicleReadActiveReservation: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      vehicleReadReservationHistory: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      vehicleReserve: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      updateVehicle: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      viewBusinessCasePurchase: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      viewBusinessCaseSelling: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      interestCreate: {
        // interest create does not haves scope defined
        participation: 'SKIP',
        // interest create does not haves scope defined
        branchId: 'SKIP',
      },
      serviceCaseCreate: {
        // Handled on BE
        serviceCaseType: 'SKIP',
      },
    },
  });

  const {isWorkshopModuleEnabled} = useIsLicenceEnabled();
  const isSalesModuleEnabled = canReadBusinessCase;
  const isSaleVehicleDisabledForSale =
    saleVehicle?.buyingState === 'not-bought' ||
    saleVehicle?.buyingState === 'returned-to-customer' ||
    (saleVehicle?.buyingState === 'buying' && saleVehicle?.type === 'BROKERAGE') ||
    (saleVehicle?.buyingState === 'priced' && saleVehicle?.type === 'BROKERAGE');

  const canCreatePurchaseBusinessCase =
    hasCreateBusinessCasePurchasePermission && hasAddVehicleToBuyPermission;
  const canCreateSellBusinessCase =
    hasCreateBusinessCaseSellingPermission &&
    hasAddVehicleToSellPermission &&
    isFalse(isSaleVehicleDisabledForSale);

  const isSaleVehicle = isNotNil(saleVehicle);

  const isArchived = isTrue(vehicle?.isArchived);
  const handleArchiveVehicle = isArchived ? unarchiveVehicle : archiveVehicle;
  const isArchiveLoading = isLoadingArchiveVehicle || isLoadingUnarchiveVehicle;

  const handleDeleteReservation = () =>
    openDeleteDialog({
      onConfirm: () => cancelSaleVehicleReservation({vehicleId}).unwrap().catch(handleApiError),
      text: i18n.t('entity.vehicle.labels.cancelCurrentReservation'),
    });

  const openReservationWizard = () =>
    openDialog(
      <VehicleReservationWizard
        vehicleId={vehicleId}
        onComplete={closeCurrentDialog}
        onCancel={closeCurrentDialog}
        data-testid={testIds.vehicles.detail('reservation')}
      />,
      {
        'data-testid': testIds.vehicles.detail('reservation'),
        title: i18n.t('entity.vehicle.labels.reservation'),
        size: 'large',
      }
    );

  const onClickBuy = () => {
    openDialog(<VehicleBuyForm vehicleId={vehicleId} />, {
      id: 'vehicleBuy-dialog',
      title: i18n.t('entity.vehicle.actions.addVehicleToBuy'),
      'data-testid': 'vehicleBuy-dialog',
    });
  };

  const onClickSell = () => {
    const navigateToCreateBusinessCaseConcept = (branchId?: string) =>
      navigate({
        pathname: businessCaseRoutes.concept,
        search: createSearchParams({
          vehicleId,
          ...(isNotNil(branchId) && {[queryParams.BRANCH_ID]: branchId}),
        }).toString(),
      });

    const confirmBranchAndNavigate = () =>
      confirmCreationBranch('business-case', navigateToCreateBusinessCaseConcept);

    if (saleVehicle?.activeReservation?.paidAdvanceDeposit) {
      openConfirmDialog({
        text: i18n.t('entity.vehicle.labels.reservationWithAdvanceDepositExistsConfirmation'),
        onConfirm: confirmBranchAndNavigate,
      });
    } else {
      confirmBranchAndNavigate();
    }
  };

  const openNewServiceCase = () =>
    navigate(
      composePath(workshopRoutes.createServiceCase, {
        queryParams: {vehicleId},
        isSearchParamsPreserved: false,
      })
    );

  const onClickCreateInterest = () =>
    confirmCreationBranch('interest', (branchId) =>
      navigate(
        composePath(interestRoutes.create, {
          queryParams: {[queryParams.VEHICLE_ID]: vehicleId, [queryParams.BRANCH_ID]: branchId},
        })
      )
    );

  return buildArray<EntityHeaderAction>()
    .when(secondaryActions.length > 0, {
      type: 'dropdown-iconButton',
      priority: 'secondary',
      menuItems: secondaryActions,
      'data-testid': testIds.vehicles.detail('vehicleHeader-more'),
    })
    .when(
      saleVehicle?.activeReservation &&
        !saleVehicle?.activeReservation?.paidAdvanceDeposit &&
        isSaleVehicle &&
        canCancelReservation &&
        canReadActiveReservation &&
        canReadReservationHistory &&
        isSalesModuleEnabled,
      {
        type: 'button',
        variant: 'secondary',
        title: i18n.t('entity.vehicle.actions.cancelReservation'),
        onClick: handleDeleteReservation,
        'data-testid': testIds.vehicles.detail('vehicleHeader-reservation'),
      }
    )
    .when(
      (isSaleActionEnabled('SALE_VEHICLE_RESERVE') || saleVehicle?.activeReservation) &&
        isSaleVehicle &&
        canReserve &&
        canReadActiveReservation &&
        canReadReservationHistory &&
        canUpdateReservation &&
        isSalesModuleEnabled,
      {
        type: 'button',
        variant: 'secondary',
        title: i18n.t('entity.vehicle.labels.reservation'),
        onClick: openReservationWizard,
        'data-testid': testIds.vehicles.detail('vehicleHeader-reservation'),
      }
    )
    .when(isSaleVehicle && isSalesModuleEnabled && canCreateInterest, {
      type: 'button',
      variant: 'secondary',
      title: i18n.t('entity.interest.labels.newInterest'),
      onClick: onClickCreateInterest,
      'data-testid': testIds.vehicles.detail('vehicleHeader-createInterest'),
    })
    .when(
      isSaleActionEnabled('SALE_VEHICLE_BUY') &&
        isSalesModuleEnabled &&
        isSaleVehicle &&
        canReadPurchaseVehicle &&
        canCreatePurchaseBusinessCase,
      {
        type: 'button',
        variant: isSaleActionEnabled('SALE_VEHICLE_SELL') ? 'secondary' : 'primary',
        title: i18n.t('general.actions.buy'),
        onClick: onClickBuy,
        'data-testid': testIds.vehicles.detail('vehicleHeader-buy'),
      }
    )
    .when(
      isSalesModuleEnabled &&
        isSaleActionEnabled('SALE_VEHICLE_SELL') &&
        isSaleVehicle &&
        canReadSellVehicle &&
        canCreateSellBusinessCase,
      {
        type: 'button',
        variant: 'primary',
        title: i18n.t('general.actions.sell'),
        onClick: onClickSell,
        'data-testid': testIds.vehicles.detail('vehicleHeader-sell'),
      }
    )
    .when(!isSaleVehicle && isWorkshopModuleEnabled && canUpdateVehicle, {
      type: 'button',
      variant: 'secondary',
      title: isArchived
        ? i18n.t('entity.vehicle.labels.unarchive')
        : i18n.t('entity.vehicle.labels.archive'),
      onClick: () => handleArchiveVehicle({vehicleId}),
      isLoading: isArchiveLoading,
      'data-testid': testIds.vehicles.detail(
        isArchived ? 'vehicleHeader-unarchive' : 'vehicleHeader-archive'
      ),
    })
    .when(isWorkshopModuleEnabled && canCreateServiceCase && !isSaleVehicle, {
      type: 'button',
      variant: 'primary',
      title: i18n.t('page.workshop.labels.newServiceCase'),
      onClick: openNewServiceCase,
      'data-testid': testIds.vehicles.detail('vehicleHeader-create-service-case'),
    });
};
